import React, { useRef, useState, useEffect } from 'react';
import * as uuid from 'uuid';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha } from '@mui/material/styles';
import ContentContainer from '../../layout/ContentContainer.jsx';
import DocumentSlot from './DocumentSlot.jsx';
import GuidedDocumentCamera from './GuidedDocumentCamera.jsx';
import DocumentReviewFeedback from './DocumentReviewFeedback.jsx';
import { uploadDocument } from './uploadDocument.js';
import { buildProblems } from '../../util/documentProblems.js';
import { ID_DOCUMENT_TYPES, sidesForType, miniDocVariant } from './idDocumentTypes.js';

const SIDE_LABEL_KEY = { front: 'slotFront', back: 'slotBack', single: 'slotPhotoPage' };
const CAM_HEADER_KEY = { front: 'camHeaderFront', back: 'camHeaderBack', single: 'camHeaderPhotoPage' };

// Runtime guard for the formats the combiner/thumbnail handle (`accept` is only a hint); HEIC excluded.
const SUPPORTED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

// Guaranteed minimum for the processing state so an instant response reads as a deliberate check, not a strobe.
const MIN_VERIFY_MS = 800;

const toObjectUrl = (blob) => { try { return URL.createObjectURL(blob); } catch { return ''; } };

// Touch-primary ≈ phone/tablet → camera is the emphasised default; fine-pointer desktop → upload default.
const isTouchPrimary = () =>
	typeof window !== 'undefined' &&
	(window.matchMedia?.('(pointer: coarse)')?.matches || (navigator.maxTouchPoints || 0) > 0);

const DocumentSlotCapture = ({
	type, allowedTypes, text, context, uploadUrl,
	useCamera = true, forceCamera = false, cameraIncludes, cameraRotationAngle,
	reviewMessages, addPendingAction = () => {}, removePendingAction = () => {},
	onComplete, disabled, minVerifyMs = MIN_VERIFY_MS,
}) => {
	const sides = sidesForType(type);
	const meta = ID_DOCUMENT_TYPES[type] || {};
	const docVariant = miniDocVariant(type);
	const typeName = text[meta.labelKey] || type;

	const blobs = useRef({});
	const fileInputRef = useRef(null);
	const pendingSideRef = useRef(null);
	const thumbsRef = useRef({});
	const [thumbs, setThumbs] = useState({});
	const [status, setStatus] = useState({});
	const [openCamera, setOpenCamera] = useState(null);
	const [parsed, setParsed] = useState(null);
	const [isParsing, setParsing] = useState(false);
	const [error, setError] = useState(null);
	thumbsRef.current = thumbs;

	// Revoke thumbnail object URLs on unmount so ID-photo blobs are not pinned in memory (esp. kiosks).
	useEffect(() => () => { Object.values(thumbsRef.current).forEach((u) => u && URL.revokeObjectURL(u)); }, []);

	const openCameraFor = (side) => setOpenCamera(side);
	const openFileFor = (side) => { pendingSideRef.current = side; fileInputRef.current?.click(); };
	const cameraEnabled = useCamera;
	const cameraPrimary = useCamera && (forceCamera || isTouchPrimary());

	// Verification is a deliberate step: the user reviews both filled rows, then taps "Verify document".
	const runUpload = async () => {
		// Upload from blobs, not status/thumbs: refuse if a required side's blob is missing.
		if (!sides.every((sd) => blobs.current[sd])) { setError(text.error); return; }
		setParsing(true);
		setError(null);
		const action = uuid.v4();
		addPendingAction(action);
		const startedAt = Date.now();
		try {
			const file = await uploadDocument({ type, sides: blobs.current, context, allowedTypes, uploadUrl });
			const elapsed = Date.now() - startedAt;
			if (elapsed < minVerifyMs) await new Promise((resolve) => setTimeout(resolve, minVerifyMs - elapsed));
			setParsed(file);
			onComplete(file);
		} catch (err) {
			// Never String() a non-Error (a DOM Event stringifies to the useless "[object Event]").
			setError(err?.message || text.error);
			onComplete(null);
		} finally {
			removePendingAction(action);
			setParsing(false);
		}
	};

	const handleCapture = (side, blob) => {
		blobs.current[side] = blob;
		setThumbs((t) => { if (t[side]) URL.revokeObjectURL(t[side]); return { ...t, [side]: toObjectUrl(blob) }; });
		setStatus((s) => ({ ...s, [side]: 'done' }));
		setOpenCamera(null);
		setError(null);
		// Any (re)capture invalidates a prior verdict: clear it (and the parent's doc) so the user re-verifies.
		if (parsed) { setParsed(null); onComplete(null); }
	};

	const revertToSlots = () => {
		Object.values(thumbs).forEach((u) => u && URL.revokeObjectURL(u));
		blobs.current = {};
		setParsed(null);
		setStatus({});
		setThumbs({});
		setError(null);
		onComplete(null);
	};

	const onFileChange = (e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file || !pendingSideRef.current) return;
		if (!SUPPORTED_UPLOAD_TYPES.includes(file.type)) { setError(text.unsupportedImageFormat); return; }
		handleCapture(pendingSideRef.current, file);
	};

	const allSidesFilled = sides.every((sd) => status[sd] === 'done');
	const problems = parsed?.status === 'inReview' ? buildProblems(parsed, reviewMessages) : [];
	const flagged = problems.length > 0;
	// A finished verify with nothing to acknowledge → positive confirmation (green box).
	// Covers both `accepted` and `inReview`-without-problems (provisionally accepted).
	const verified = !!parsed && !flagged;
	const provisional = verified && parsed.status === 'inReview';

	const renderSlot = (side) => (
		<DocumentSlot
			key={side}
			side={side}
			label={text[SIDE_LABEL_KEY[side]]}
			status={status[side] || 'empty'}
			thumb={thumbs[side]}
			thumbIsPdf={blobs.current[side]?.type === 'application/pdf'}
			disabled={disabled || isParsing}
			text={text}
			miniDocVariant={docVariant}
			band={!!meta.band}
			cameraEnabled={cameraEnabled}
			cameraPrimary={cameraPrimary}
			forceCamera={forceCamera}
			onCamera={() => openCameraFor(side)}
			onFile={() => openFileFor(side)}
		/>
	);

	return (
		<ContentContainer>
			{/* Breadcrumb (passive) — going back to re-pick the type is SectionNav's "Previous" button. */}
			<Box sx={{ mb: 2 }}>
				<Typography variant="caption" color="text.secondary">{text.chooseTypeCrumb} ›</Typography>
				<Typography variant="h6">{typeName}</Typography>
			</Box>

			{flagged ? (
				// FLAGGED: the two rows + verify answer read as one document-scoped container, tinted `error`
				// (red) to match the "Continue anyway" button inside the drawer. Rows inside stay neutral.
				<Box
					sx={(theme) => ({
						border: 2,
						borderColor: 'error.main',
						borderRadius: 2,
						backgroundColor: alpha(theme.palette.error.main, 0.06),
						p: 1.5,
						mt: 1,
					})}
				>
					{sides.map((side) => renderSlot(side))}
					<Divider sx={{ my: 1 }} />
					<DocumentReviewFeedback
						variant="expanded"
						problems={problems}
						copy={reviewMessages}
						acknowledged={!!parsed.reviewAcknowledged}
						onConfirm={() => {
							// Update local state too (not just the parent) so the drawer collapses in place.
							const acknowledgedFile = { ...parsed, reviewAcknowledged: true };
							setParsed(acknowledgedFile);
							onComplete(acknowledgedFile);
						}}
						onCancel={() => { revertToSlots(); }}
					/>
				</Box>
			) : verified ? (
				// VERIFIED: same container shape as the flagged box, tinted `success` (green) to mirror it.
				// The verdict sits BELOW the rows — the same position as the flagged feedback and the "Verify
				// document" button it replaces — so the outcome always appears where the user is looking. Kept to
				// one quiet line (icon + label, no actions) so success stays lighter than a flagged error.
				<Box
					data-testid="document-verified"
					role="status"
					aria-live="polite"
					sx={(theme) => ({
						border: 2,
						borderColor: 'success.main',
						borderRadius: 2,
						backgroundColor: alpha(theme.palette.success.main, 0.06),
						p: 1.5,
						mt: 1,
					})}
				>
					{sides.map((side) => renderSlot(side))}
					<Divider sx={{ my: 1 }} />
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<CheckCircleIcon sx={{ color: 'success.main', flexShrink: 0 }} />
						<Typography sx={{ fontWeight: 600, color: 'success.main' }}>
							{provisional ? text.feedbackReview : text.documentVerified}
						</Typography>
					</Box>
				</Box>
			) : (
				<>
					{/* Rows dim + lock while verifying; the CTA/result lives in one fixed slot below to avoid reflow. */}
					<Box
						aria-busy={isParsing}
						sx={{ opacity: isParsing ? 0.6 : 1, pointerEvents: isParsing ? 'none' : 'auto', transition: 'opacity 0.2s' }}
					>
						{sides.map((side) => renderSlot(side))}
					</Box>

					{isParsing && (
						<Box role="status" aria-live="polite" sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
							<Typography sx={{ fontWeight: 600, mb: 0.5 }}>{text.verifyingTitle}</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{text.verifyingBody}</Typography>
							<LinearProgress />
						</Box>
					)}

					{error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

					{allSidesFilled && !isParsing && !parsed && (
						<Box sx={{ mt: 2 }}>
							<Button data-testid="verify-document" variant="contained" fullWidth size="large" onClick={runUpload} disabled={disabled}>
								{text.verifyDocument}
							</Button>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
								{text.verifyHint}
							</Typography>
						</Box>
					)}
				</>
			)}

			{openCamera && (
				<GuidedDocumentCamera
					side={openCamera}
					aspect={meta.aspect || 'id-1'}
					header={text[CAM_HEADER_KEY[openCamera]]}
					text={text}
					cameraIncludes={cameraIncludes}
					cameraRotationAngle={cameraRotationAngle}
					onCapture={(blob) => handleCapture(openCamera, blob)}
					onClose={() => setOpenCamera(null)}
					onPickFile={() => { pendingSideRef.current = openCamera; setOpenCamera(null); fileInputRef.current?.click(); }}
				/>
			)}

			<input ref={fileInputRef} type="file" accept="image/jpeg,image/png,application/pdf" style={{ display: 'none' }} onChange={onFileChange} />
		</ContentContainer>
	);
};

export default DocumentSlotCapture;
